<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { TargetingService } from '@client/services/targeting';

  import { appStore } from '@client/stores/app';

  import { TargetingList } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const targetingTypes = [
    { label: $format('label.GROUP'), value: 'group' },
    { label: $format('label.DOMAIN'), value: 'domain' },
    { label: $format('label.RECIPIENT'), value: 'recipient' }
  ];

  let loading = true;
  let loadingType = false;
  let selectedType = targetingTypes[0];
  let selectedTargetId = null;
  let targets = [];
  let allTargeting = {};

  const loadTargeting = async () => {
    const targeting = await TargetingService.get();
    allTargeting = targeting || { accounts: [], departments: [] };

    if (selectedType.value === 'group') {
      targets = (allTargeting.departments || []).map((dept, index) => ({
        ...dept,
        _id: dept._id || `dept_${index}`,
        priority: index + 1
      }));
    } else {
      const accounts = allTargeting.accounts || [];
      if (selectedType.value === 'domain') {
        targets = accounts.filter((acc) => acc.name.includes('@') === false);
      } else {
        targets = accounts.filter((acc) => acc.name.includes('@') === true);
      }
    }
  };

  const selectTarget = (targetId) => {
    selectedTargetId = targetId;
  };

  const handleTypeChange = async () => {
    loadingType = true;
    selectedTargetId = null;
    await loadTargeting();
    loadingType = false;
  };

  const executeDelete = async () => {
    $appStore.loading = true;

    try {
      // Fetch fresh targeting to avoid overwriting concurrent changes
      const currentTargeting = await TargetingService.get();
      let targetToDelete;
      let updatedTargeting;

      if (selectedType.value === 'group') {
        targetToDelete = targets.find((t) => t._id === selectedTargetId);
        const departments = currentTargeting.departments.filter(
          (dept) => (dept._id || `dept_${currentTargeting.departments.indexOf(dept)}`) !== selectedTargetId
        );

        updatedTargeting = {
          accounts: currentTargeting.accounts || [],
          departments
        };
      } else {
        // For domains/recipients - matches accounts.svelte onDelete behavior
        // Clear campaigns and tags but keep the account
        targetToDelete = targets.find((t) => t.name === selectedTargetId);

        const accounts = currentTargeting.accounts.map((acc) => {
          if (acc.name === selectedTargetId) {
            return { name: acc.name, ads: [], tags: [] };
          }
          return acc;
        });

        updatedTargeting = {
          accounts,
          departments: currentTargeting.departments || []
        };
      }

      await TargetingService.update(updatedTargeting);

      notify($format('label.SUCCESS.TARGET_DELETED'));

      step.data = {
        id: selectedType?.value === 'group' ? 'Groups' : selectedType?.value === 'domain' ? 'Domains' : 'Recipients',
        rollbackData: {
          targetToDelete,
          allTargeting: currentTargeting
        }
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.DELETE_TARGET'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackDelete = async () => {
    $appStore.loading = true;

    try {
      const { allTargeting } = step.data.rollbackData;

      if (!allTargeting) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      await TargetingService.update(allTargeting);

      notify($format('label.SUCCESS.ROLLBACK_TARGET_DELETION'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_TARGET_DELETION'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      await loadTargeting();
    } catch (error) {
      console.error('Failed to load targeting data:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeDelete);
    }

    if (onRollback) {
      onRollback(rollbackDelete);
    }
  });

  $: nextDisabled = !selectedTargetId || loading || loadingType;
</script>

<div class="targeting-delete-form">
  <div class="type-selector">
    <p>{$format('label.TARGET_TYPE')}</p>
    <Select
      items={targetingTypes}
      bind:value={selectedType}
      clearable={false}
      showChevron
      on:change={handleTypeChange}
    />
  </div>

  <div class="target-list">
    <TargetingList
      {targets}
      bind:selectedTargetId
      onSelectTarget={selectTarget}
      targetType={selectedType.value}
      loading={loadingType || loading}
    />
  </div>
</div>

<style lang="scss">
  .targeting-delete-form {
    .type-selector {
      margin-bottom: var(--margin-large);

      p {
        font-weight: var(--font-weight-medium);
        margin-bottom: var(--margin-base);
      }
    }

    .target-list {
      margin-top: var(--margin-large);
    }

    :global {
      .target {
        margin-bottom: var(--margin-base);
      }
    }
  }
</style>
